/**
 * Geolocation Utility Functions
 * Provides distance calculations, geofence validation, and location helpers
 */

/**
 * Convert degrees to radians
 * @param {number} degrees
 * @returns {number} Radians
 */
function toRadians(degrees) {
  return degrees * (Math.PI / 180);
}

/**
 * Calculate distance between two coordinates using Haversine formula
 * @param {number} lat1 - Latitude of point 1
 * @param {number} lon1 - Longitude of point 1
 * @param {number} lat2 - Latitude of point 2
 * @param {number} lon2 - Longitude of point 2
 * @returns {number} Distance in meters
 */
export function calculateDistance(lat1, lon1, lat2, lon2) {
  const R = 6371000; // Earth's radius in meters
  const dLat = toRadians(lat2 - lat1);
  const dLon = toRadians(lon2 - lon1);

  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(toRadians(lat1)) *
      Math.cos(toRadians(lat2)) *
      Math.sin(dLon / 2) *
      Math.sin(dLon / 2);

  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
}

/**
 * Check if a position is within a geofence
 * @param {Object} position - Current position {latitude, longitude}
 * @param {Object} geofence - Geofence config {latitude, longitude, radiusMeters}
 * @returns {Object} {isWithin: boolean, distance: number (in meters)}
 */
export function checkGeofence(position, geofence) {
  if (!position || !geofence) {
    return { isWithin: false, distance: null };
  }

  const distance = calculateDistance(
    position.latitude,
    position.longitude,
    geofence.latitude,
    geofence.longitude
  );

  return {
    isWithin: distance <= geofence.radiusMeters,
    distance: Math.round(distance)
  };
}

/**
 * Format distance for display
 * @param {number} meters - Distance in meters
 * @returns {string} Formatted distance string
 */
export function formatDistance(meters) {
  if (meters == null) return 'Unknown';
  if (meters < 1000) {
    return `${Math.round(meters)}m`;
  }
  return `${(meters / 1000).toFixed(1)}km`;
}

/**
 * Get current position with Promise API
 * @param {Object} options - Geolocation options
 * @returns {Promise<GeolocationPosition>}
 */
export function getCurrentPosition(options = {}) {
  return new Promise((resolve, reject) => {
    if (!navigator.geolocation) {
      reject(new Error('Geolocation is not supported by this browser'));
      return;
    }

    const defaultOptions = {
      enableHighAccuracy: true,
      timeout: 15000,
      maximumAge: 60000 // 1 minute cache
    };

    navigator.geolocation.getCurrentPosition(
      resolve,
      (error) => {
        let message;
        switch (error.code) {
          case error.PERMISSION_DENIED:
            message = 'Location permission denied';
            break;
          case error.POSITION_UNAVAILABLE:
            message = 'Location information unavailable';
            break;
          case error.TIMEOUT:
            message = 'Location request timed out';
            break;
          default:
            message = 'Unknown location error';
        }
        reject(new Error(message));
      },
      { ...defaultOptions, ...options }
    );
  });
}

/**
 * Check if geolocation is supported
 * @returns {boolean}
 */
export function isGeolocationSupported() {
  return 'geolocation' in navigator;
}

/**
 * Check if geolocation permission is granted
 * @returns {Promise<string>} 'granted', 'denied', or 'prompt'
 */
export async function checkGeolocationPermission() {
  if (!navigator.permissions) {
    // Fallback for browsers without Permissions API
    return 'prompt';
  }

  try {
    const result = await navigator.permissions.query({ name: 'geolocation' });
    return result.state;
  } catch {
    return 'prompt';
  }
}

/**
 * Request geolocation permission with user-friendly flow
 * @returns {Promise<{granted: boolean, position?: Object, error?: string}>}
 */
export async function requestGeolocationPermission() {
  try {
    const position = await getCurrentPosition();
    return {
      granted: true,
      position: {
        latitude: position.coords.latitude,
        longitude: position.coords.longitude,
        accuracy: position.coords.accuracy,
        timestamp: position.timestamp
      }
    };
  } catch (error) {
    return { granted: false, error: error.message };
  }
}

/**
 * Get accuracy description based on meters
 * @param {number} accuracy - Accuracy in meters
 * @returns {Object} {label: string, color: string}
 */
export function getAccuracyInfo(accuracy) {
  if (accuracy == null) {
    return { label: 'Unknown', color: 'text-gray-500', bg: 'bg-gray-100' };
  }
  if (accuracy <= 10) {
    return { label: 'Excellent', color: 'text-green-700', bg: 'bg-green-100' };
  }
  if (accuracy <= 30) {
    return { label: 'Good', color: 'text-green-600', bg: 'bg-green-50' };
  }
  if (accuracy <= 100) {
    return { label: 'Fair', color: 'text-yellow-600', bg: 'bg-yellow-50' };
  }
  return { label: 'Poor', color: 'text-red-600', bg: 'bg-red-50' };
}

/**
 * Watch position with callback
 * @param {Function} onSuccess - Called with position on each update
 * @param {Function} onError - Called on error
 * @param {Object} options - Geolocation options
 * @returns {number} Watch ID (use to clear with navigator.geolocation.clearWatch)
 */
export function watchPosition(onSuccess, onError, options = {}) {
  if (!navigator.geolocation) {
    onError?.(new Error('Geolocation is not supported'));
    return null;
  }

  const defaultOptions = {
    enableHighAccuracy: true,
    timeout: 30000,
    maximumAge: 10000
  };

  return navigator.geolocation.watchPosition(
    (position) => {
      onSuccess?.({
        latitude: position.coords.latitude,
        longitude: position.coords.longitude,
        accuracy: position.coords.accuracy,
        timestamp: position.timestamp
      });
    },
    (error) => {
      let message;
      switch (error.code) {
        case error.PERMISSION_DENIED:
          message = 'Location permission denied';
          break;
        case error.POSITION_UNAVAILABLE:
          message = 'Location information unavailable';
          break;
        case error.TIMEOUT:
          message = 'Location request timed out';
          break;
        default:
          message = 'Unknown location error';
      }
      onError?.(new Error(message));
    },
    { ...defaultOptions, ...options }
  );
}

/**
 * Clear position watch
 * @param {number} watchId - Watch ID from watchPosition
 */
export function clearWatch(watchId) {
  if (watchId != null && navigator.geolocation) {
    navigator.geolocation.clearWatch(watchId);
  }
}
